import { logbookRoutesMESF, MESF_CONFIGURATIONS } from "@dexteel/mesf-core";
import { configurationPath } from "../configurations";
import { adjustmentRoutes } from "../routes/adjustment.routes";
import { calendarRoute } from "../routes/calendar.routes";
import { catalogRoutes } from "../routes/catalog.routes";
import { delayRoutes } from "../routes/delay.routes";
import { inspectionRoutes } from "../routes/inspection.routes";
import { formRoutes } from "../routes/forms.routes";
import { labRoutes } from "../routes/lab.routes";
import {
	millRoutes,
	routeMillGantt,
	routeMillOverview2,
	routeMillSchedule,
} from "../routes/mill.routes";
import { productsM1Routes, productsRoutes } from "../routes/products.routes";
import { reportRoutes } from "../routes/report.routes";
import { rollshopRoutes } from "../routes/rollshop.routes";
import { salesRoutes } from "../routes/sales.routes";
import { stackerPulpitRoutes } from "../routes/stacker-pulpit.routes";
import { MILL_1, MILL_2, MillConfig } from "./mill-config";
import { calendarYearRoute } from "../routes/calendar-year.routes";
import {
	m1Routes,
	routeM1Gantt,
	routeM1Incentive,
	routeM1IncentiveManager,
	routeM1Overview,
	routeM1Schedule,
	routeM1ShiftReport,
} from "../routes/m1.routes";
import {
	incentiveRoutes,
	routeIncentive,
	routeIncentiveManager,
} from "../routes/incentive.routes";
import {
	routeShiftReport,
	shiftReportRoutes,
} from "../routes/shift_report.routes";
import { utilityRoutes } from "../routes/utility.routes";

export type ConfigurationPath =
	| (typeof MESF_CONFIGURATIONS)[number]
	| "delay-categories"
	| "delay-codes"
	| "locations"
	| "sections"
	| "section-dimensions"
	| "section-families"
	| "adjustments-types"
	| "adjustments-reasons"
	| "tensile-requirements"
	| "tests-by-section"
	| "chemistry-ranges"
	| "shift-report-config"
	| "heat-age-thresholds"
	| "water-allocations"
	| "gas-allocations"
	| "inspection-templates"
	| "surface-defects"
	| "water-meter-thresholds";

export interface MillExtendedConfig extends MillConfig {
	routes: Array<{ path: string; element: React.ReactNode }[]>;
	homePath: string;
	configurations: ConfigurationPath[];
}

export const MILL_1_CONFIG: MillExtendedConfig = {
	...MILL_1,
	routes: [
		delayRoutes,
		logbookRoutesMESF,
		calendarRoute,
		salesRoutes,
		calendarYearRoute,
		m1Routes,
		productsM1Routes,
		labRoutes,
		inspectionRoutes,
	],
	homePath: routeM1Overview,
	configurations: [
		...MESF_CONFIGURATIONS,
		"locations",
		"delay-categories",
		"delay-codes",
		"sections",
		"section-dimensions",
		"tensile-requirements",
		"tests-by-section",
		"chemistry-ranges",
		"shift-report-config",
		"heat-age-thresholds",
		"water-allocations",
		"gas-allocations",
		"inspection-templates",
		"surface-defects",
		"water-meter-thresholds",
	],
};

export const MILL_2_CONFIG: MillExtendedConfig = {
	...MILL_2,
	routes: [
		millRoutes,
		adjustmentRoutes,
		catalogRoutes,
		formRoutes,
		reportRoutes,
		rollshopRoutes,
		shiftReportRoutes,
		incentiveRoutes,
		stackerPulpitRoutes,
		productsRoutes,
	],
	homePath: routeMillOverview2,
	configurations: [
		...MESF_CONFIGURATIONS,
		"delay-categories",
		"delay-codes",
		"locations",
		"sections",
		"section-dimensions",
		"section-families",
		"adjustments-types",
		"adjustments-reasons",
		"tensile-requirements",
		"tests-by-section",
		"chemistry-ranges",
		"shift-report-config",
		"heat-age-thresholds",
		"water-allocations",
		"gas-allocations",
		"inspection-templates",
		"surface-defects",
		"water-meter-thresholds",
	],
};

export const getMillConfigByAssetId = (
	assetId?: number,
): MillExtendedConfig => {
	if (assetId === MILL_1.id) {
		return MILL_1_CONFIG;
	}
	return MILL_2_CONFIG;
};

export const getHomePathByAssetId = (assetId?: number): string => {
	return getMillConfigByAssetId(assetId).homePath;
};

// Shared routes available for BOTH mills (delays, logbook, calendar, lab, etc.)
// These should never trigger a mill-based redirect.
const SHARED_ROUTES = [
	...delayRoutes,
	...logbookRoutesMESF,
	...calendarRoute,
	...calendarYearRoute,
	...salesRoutes,
	...labRoutes,
	...utilityRoutes,
];

const normalizePath = (path: string): string =>
	path.startsWith("/") ? path : `/${path}`;

const matchesRoute = (pathname: string, path: string): boolean => {
	const base = normalizePath(path);
	const regex = new RegExp(`^${base.replace(/:[^/]+/g, "[^/]+")}(?:/|$)`);
	return regex.test(pathname);
};

const isSharedRoute = (pathname: string): boolean =>
	SHARED_ROUTES.some((route) => matchesRoute(pathname, route.path));

export const isRouteAllowedForMill = (
	pathname: string,
	assetId?: number,
): boolean => {
	if (isSharedRoute(pathname)) return true;
	if (matchesRoute(pathname, "/account")) return true;
	if (pathname === configurationPath) return true;

	const config = getMillConfigByAssetId(assetId);

	const validRoute = config.routes
		.flat()
		.some((route) => matchesRoute(pathname, route.path));

	const validConfig = config.configurations.some((configuration) =>
		matchesRoute(pathname, `${configurationPath}/${configuration}`),
	);

	return validRoute || validConfig;
};

export const isConfigurationAllowed = (
	configPath: ConfigurationPath,
	assetId?: number,
): boolean => {
	const config = getMillConfigByAssetId(assetId);
	return config.configurations.includes(configPath);
};

/**
 * Maps equivalent pages between M1 and M2 so that switching mills
 * navigates to the same "concept" page instead of always going home.
 * Key: route prefix to match, Value: route to navigate to.
 */
const MILL_1_TO_MILL_2: [string, string][] = [
	[routeM1IncentiveManager, routeIncentiveManager],
	[routeM1Incentive, routeIncentive],
	[routeM1Overview, routeMillOverview2],
	[routeM1Schedule, routeMillSchedule],
	[routeM1ShiftReport, routeShiftReport],
	[routeM1Gantt, routeMillGantt],
];

const MILL_2_TO_MILL_1: [string, string][] = [
	[routeIncentiveManager, routeM1IncentiveManager],
	[routeIncentive, routeM1Incentive],
	["/mill/overview", routeM1Overview],
	[routeMillSchedule, routeM1Schedule],
	[routeShiftReport, routeM1ShiftReport],
	[routeMillGantt, routeM1Gantt],
];

/**
 * Given the current pathname and the target mill's assetId,
 * returns the equivalent route in the other mill — or the home path as fallback.
 */
export const getEquivalentRoute = (
	pathname: string,
	targetAssetId?: number,
): string => {
	const mappings =
		targetAssetId === MILL_1.id ? MILL_2_TO_MILL_1 : MILL_1_TO_MILL_2;

	for (const [prefix, target] of mappings) {
		if (pathname.startsWith(prefix)) {
			return target;
		}
	}

	return getHomePathByAssetId(targetAssetId);
};
